/**
 * PDF export layout picker for Uni Forms report templates.
 *
 * The server paginates a landscape A4 table by default. These options
 * shrink the table uniformly (Excel-style "Fit to") so all rows, all
 * columns, or the complete report sit on one page.
 */
import React from 'react';
import { dottedDate, UniModal } from './UniShared.jsx';

export const PDF_SCALE_AS_IS = 'as_is';

/** Fallback download name when the PDF response has no Content-Disposition. */
export function reportPdfFallbackName(reportName) {
  const stem = String(reportName || 'report').trim() || 'report';
  return `${stem}-${dottedDate()}.pdf`;
}

export const PDF_SCALE_OPTIONS = [
  {
    value: 'as_is',
    label: 'As it is',
    hint: 'Keep the current size. Extra rows and columns continue on further pages.',
  },
  {
    value: 'rows',
    label: 'All rows on one page',
    hint: 'Zoom out so every row fits the page height and further columns fill the page width. Extra columns may still use more pages.',
  },
  {
    value: 'columns',
    label: 'All columns on one page',
    hint: 'Shrink so every column fits the page width. Extra rows may still use more pages.',
  },
  {
    value: 'both',
    label: 'Complete report on one page',
    hint: 'Shrink so the whole table fits on a single page.',
  },
];

export function ReportPdfExportModal({
  open,
  busy = false,
  onClose,
  onExport,
}) {
  const [scale, setScale] = React.useState(PDF_SCALE_AS_IS);
  const groupId = React.useId();

  React.useEffect(() => {
    if (open) setScale(PDF_SCALE_AS_IS);
  }, [open]);

  const close = () => {
    if (!busy) onClose?.();
  };

  const submit = () => {
    if (busy) return;
    onExport?.(scale);
  };

  return (
    <UniModal
      open={open}
      title="Export PDF"
      onClose={close}
      width={560}
      footer={(
        <>
          <button
            type="button"
            className="ty-btn ty-btn--secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ty-btn"
            onClick={submit}
            disabled={busy}
          >
            {busy ? 'Exporting…' : 'Export PDF'}
          </button>
        </>
      )}
    >
      <p className="uni-card__sub" style={{ marginTop: 0 }}>
        Choose how the table is laid out on the page. Scaling never wraps
        cells — it only shrinks the report so the chosen side fits.
      </p>
      <div className="uni-pdf-scale-list" role="radiogroup" aria-label="PDF layout">
        {PDF_SCALE_OPTIONS.map((opt) => {
          const id = `${groupId}-${opt.value}`;
          const selected = scale === opt.value;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className={`uni-pdf-scale-option${selected ? ' is-selected' : ''}`}
            >
              <input
                id={id}
                type="radio"
                name={groupId}
                value={opt.value}
                checked={selected}
                onChange={() => setScale(opt.value)}
              />
              <span>
                <span className="uni-pdf-scale-option__label">{opt.label}</span>
                <span className="uni-pdf-scale-option__hint">{opt.hint}</span>
              </span>
            </label>
          );
        })}
      </div>
    </UniModal>
  );
}
